import { formatDate } from '../../../shared/utils/localization';
import type { RosterScheduleRule, RosterWeekDay, StaffScheduleStatus, WeekDayShiftEntry, WeekDayState, WeekScheduleTab } from '../types/Rostering';
import type { RosterDateLabelTranslator } from './rosterDateLabels';
import { formatWeekDayShortLabel } from './rosterDateLabels';
import { formatTimeValue, ruleAppliesToWeekDay } from './rosterUtils';
import {
  defaultScheduleStatusForTab,
  isBookableScheduleStatus,
  scheduleStatusToRuleType,
} from './scheduleStatusUtils';

export const parseWeekStartParam = (value: string | null | undefined): Date | null => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getIsoWeekNumber = (date: Date): number => {
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  target.setDate(target.getDate() + 4 - (target.getDay() || 7));
  const yearStart = new Date(target.getFullYear(), 0, 1);
  return Math.ceil((((target.getTime() - yearStart.getTime()) / 86_400_000) + 1) / 7);
};

export const parseTimeToMinutes = (value: string): number => {
  const [hours, minutes] = value.split(':').map((part) => Number(part));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
    return 0;
  }

  return hours * 60 + minutes;
};

export const createWeekShiftEntry = (
  scheduleStatus: StaffScheduleStatus = 'working',
): WeekDayShiftEntry => ({
  key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  scheduleStatus,
  type: scheduleStatusToRuleType(scheduleStatus),
  startTime: '09:00',
  endTime: '17:00',
});

export const buildWeekDayStates = (
  weekDays: RosterWeekDay[],
  rules: RosterScheduleRule[],
  employeeId: string,
  t?: RosterDateLabelTranslator,
): WeekDayState[] => {
  const employeeRules = rules.filter((rule) => rule.staffId === employeeId && rule.isEnabled);

  return weekDays.map((day) => {
    const weekdayIndex = (day.dayIndex + 6) % 7;
    const entries: WeekDayShiftEntry[] = [];

    for (const rule of employeeRules) {
      if (!ruleAppliesToWeekDay(rule, day)) {
        continue;
      }

      entries.push({
        key: `${rule.id}-${day.dayIndex}`,
        ruleId: rule.id,
        sourceDaysOfWeek: rule.daysOfWeek.length > 1 ? [...rule.daysOfWeek] : undefined,
        scheduleStatus: rule.scheduleStatus,
        type: rule.type,
        startTime: formatTimeValue(rule.startTime) || '09:00',
        endTime: formatTimeValue(rule.endTime) || '17:00',
      });
    }

    entries.sort((left, right) => left.startTime.localeCompare(right.startTime));

    const shiftEntries = entries.filter((entry) => isBookableScheduleStatus(entry.scheduleStatus));
    const onCallEntries = entries.filter((entry) => !isBookableScheduleStatus(entry.scheduleStatus));

    return {
      ...day,
      shortLabel: t
        ? formatWeekDayShortLabel(day.date, weekdayIndex, t)
        : formatDate(day.date, { weekday: 'long', day: 'numeric' }),
      isWorking: shiftEntries.length > 0,
      isOnCall: onCallEntries.length > 0,
      shiftEntries,
      onCallEntries,
    };
  });
};

export const getTabEntries = (day: WeekDayState, tab: WeekScheduleTab): WeekDayShiftEntry[] => (
  tab === 'schedule'
    ? [...day.shiftEntries, ...day.onCallEntries]
    : day.onCallEntries
);

export const isTabDayActive = (day: WeekDayState, tab: WeekScheduleTab): boolean => (
  tab === 'schedule' ? (day.isWorking || day.isOnCall) : day.isOnCall
);

export const setTabDayActive = (day: WeekDayState, tab: WeekScheduleTab, active: boolean): WeekDayState => {
  if (tab === 'schedule') {
    return {
      ...day,
      isWorking: active,
      shiftEntries: active && day.shiftEntries.length === 0
        ? [createWeekShiftEntry('working')]
        : active ? day.shiftEntries : [],
    };
  }

  return {
    ...day,
    isOnCall: active,
    onCallEntries: active && day.onCallEntries.length === 0
      ? [createWeekShiftEntry('holiday')]
      : active ? day.onCallEntries : [],
  };
};

export const updateTabEntry = (
  day: WeekDayState,
  tab: WeekScheduleTab,
  entryKey: string,
  patch: Partial<WeekDayShiftEntry>,
): WeekDayState => {
  const applyPatch = (entry: WeekDayShiftEntry): WeekDayShiftEntry => {
    if (entry.key !== entryKey) {
      return entry;
    }

    const nextEntry = { ...entry, ...patch };
    if (patch.scheduleStatus) {
      nextEntry.type = scheduleStatusToRuleType(patch.scheduleStatus);
    }

    return nextEntry;
  };

  let nextDay: WeekDayState = {
    ...day,
    shiftEntries: day.shiftEntries.map(applyPatch),
    onCallEntries: day.onCallEntries.map(applyPatch),
  };

  if (patch.scheduleStatus) {
    const bookable = isBookableScheduleStatus(patch.scheduleStatus);
    const movedEntry = [...nextDay.shiftEntries, ...nextDay.onCallEntries].find((entry) => entry.key === entryKey);
    if (movedEntry) {
      nextDay = {
        ...nextDay,
        shiftEntries: bookable
          ? [...nextDay.shiftEntries.filter((entry) => entry.key !== entryKey), movedEntry]
          : nextDay.shiftEntries.filter((entry) => entry.key !== entryKey),
        onCallEntries: bookable
          ? nextDay.onCallEntries.filter((entry) => entry.key !== entryKey)
          : [...nextDay.onCallEntries.filter((entry) => entry.key !== entryKey), movedEntry],
        isWorking: bookable || nextDay.shiftEntries.some((entry) => entry.key !== entryKey),
        isOnCall: !bookable || nextDay.onCallEntries.some((entry) => entry.key !== entryKey),
      };
    }
  }

  return nextDay;
};

export const addTabEntry = (day: WeekDayState, tab: WeekScheduleTab): WeekDayState => {
  const entriesKey = tab === 'schedule' ? 'shiftEntries' : 'onCallEntries';

  return {
    ...day,
    [entriesKey]: [...day[entriesKey], createWeekShiftEntry(defaultScheduleStatusForTab(tab))],
    isWorking: tab === 'schedule' ? true : day.isWorking,
    isOnCall: tab === 'onCall' ? true : day.isOnCall,
  };
};

export const removeTabEntry = (day: WeekDayState, tab: WeekScheduleTab, entryKey: string): WeekDayState => {
  const shiftEntries = day.shiftEntries.filter((entry) => entry.key !== entryKey);
  const onCallEntries = day.onCallEntries.filter((entry) => entry.key !== entryKey);

  return {
    ...day,
    shiftEntries,
    onCallEntries,
    isWorking: shiftEntries.length > 0,
    isOnCall: onCallEntries.length > 0,
  };
};

export const calculateTabWeekHours = (days: WeekDayState[], tab: WeekScheduleTab): number => {
  const totalMinutes = days.reduce((weekTotal, day) => {
    if (!isTabDayActive(day, tab)) {
      return weekTotal;
    }

    return weekTotal + getTabEntries(day, tab).reduce((dayTotal, entry) => {
      if (tab === 'schedule' && !isBookableScheduleStatus(entry.scheduleStatus)) {
        return dayTotal;
      }

      const start = parseTimeToMinutes(entry.startTime);
      const end = parseTimeToMinutes(entry.endTime);
      return dayTotal + Math.max(0, end - start);
    }, 0);
  }, 0);

  return Math.round((totalMinutes / 60) * 10) / 10;
};
